import React from 'react';
import type { Operation, ColumnOption } from './types.ts';
import { ConditionBuilder } from './ConditionBuilder.tsx';
import { DIRECTION_OPTIONS } from './useSetEditor.ts';
import styles from './styles.less';

interface OperationBuilderProps {
  operation: Operation;
  operationIndex: number;
  operationsCount: number;
  columnOptions: ColumnOption[];
  findMatchingColumn: (attributeName: string) => ColumnOption | undefined;
  operatorsByDataType: Record<string, Array<{ label: string; value: string }>>;
  onOperationChange: (operationIndex: number, field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onConditionChange: (operationIndex: number, conditionIndex: number, field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onCaseSensitiveChange: (operationIndex: number, conditionIndex: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddCondition: (operationIndex: number) => void;
  onRemoveCondition: (operationIndex: number, conditionIndex: number) => void;
  onRemoveOperation: (operationIndex: number) => void;
}

export const OperationBuilder: React.FC<OperationBuilderProps> = ({
  operation,
  operationIndex,
  operationsCount,
  onOperationChange,
  onAddCondition,
  onRemoveOperation,
  ...conditionBuilderProps
}) => {
  return (
    <div key={operationIndex} className={styles['operation-container']}>
      <div className={styles['operation-header']}>
        <span>Operation {operationIndex + 1}</span>
        {operationsCount > 1 && (
          <button type="button" className={styles['remove-operation']} onClick={() => onRemoveOperation(operationIndex)}>
            Remove Operation
          </button>
        )}
      </div>
      <div className={styles['query-builder-field']}>
        <label htmlFor={`type-${operationIndex}`}>Type:</label>
        <select
          id={`type-${operationIndex}`}
          value={operation.type}
          onChange={onOperationChange(operationIndex, 'type')}
          className={styles['type-select']}
        >
          <option value="node">Node</option>
          <option value="edge">Edge</option>
        </select>
      </div>
      {operation.type === 'edge' && (
        <>
          <div className={styles['query-builder-field']}>
            <label htmlFor={`direction-${operationIndex}`}>Direction:</label>
            <select
              id={`direction-${operationIndex}`}
              value={operation.direction || 'forward'}
              onChange={onOperationChange(operationIndex, 'direction')}
              className={styles['direction-select']}
            >
              {DIRECTION_OPTIONS.map(dir => (
                <option key={dir.value} value={dir.value}>{dir.label}</option>
              ))}
            </select>
          </div>
          <div className={styles['query-builder-field']}>
            <label htmlFor={`hops-${operationIndex}`}>Hops:</label>
            <input
              id={`hops-${operationIndex}`}
              type="number"
              min="1"
              value={operation.hops || 1}
              onChange={onOperationChange(operationIndex, 'hops')}
              className={styles['hops-input']}
            />
          </div>
          <div className={styles['query-builder-field']}>
            <label htmlFor={`toFixedPoint-${operationIndex}`} className={styles['fixed-point-label']}>
              <input
                id={`toFixedPoint-${operationIndex}`}
                type="checkbox"
                checked={operation.toFixedPoint || false}
                onChange={onOperationChange(operationIndex, 'toFixedPoint')}
              />
              To Fixed Point
            </label>
          </div>
        </>
      )}
      <div className={styles['conditions-section']}>
        <span className={styles['conditions-title']}>Conditions ({operation.conditions.length})</span>
        {operation.conditions.map((condition, conditionIndex) => (
          <ConditionBuilder
            key={conditionIndex}
            condition={condition}
            conditionIndex={conditionIndex}
            operationIndex={operationIndex}
            operationType={operation.type}
            conditionsCount={operation.conditions.length}
            {...conditionBuilderProps}
          />
        ))}
        <button type="button" className={styles['add-condition']} onClick={() => onAddCondition(operationIndex)}>
          + Add Condition
        </button>
      </div>
    </div>
  );
};
